import type { TwoRoomConfig, TwoRoomEntryWall } from './types';

export type TwoRoomFramingWall = 'front' | 'back' | 'left' | 'right';

export const TWO_ROOM_DEFAULT_ENTRY_WALL: TwoRoomEntryWall = 'short';
export const TWO_ROOM_PANORAMA_WALL: TwoRoomFramingWall = 'right';
export const TWO_ROOM_PANORAMA_GLAZING_HEIGHT_MM = 1600;

/**
 * Long wall the wood heater (and its shields + fresh-air intake) backs against.
 * The panorama glazing always covers the right wall, so the heater moves to the
 * opposite long wall whenever panorama is active to keep it off the glass.
 */
export function getTwoRoomHeaterWall(config: TwoRoomConfig): 'left' | 'right' {
  return config.glazingStyle === 'panorama' ? 'left' : 'right';
}

/**
 * Long wall the entry door uses when `entryWall === 'long'`. It cannot be cut
 * into the full-height panorama glass (right wall), so it moves to the opposite
 * long wall when panorama is active. With a relax room the divider keeps this
 * entry clear of the heater even though both share that wall.
 */
export function getTwoRoomLongEntryWall(
  config: Pick<TwoRoomConfig, 'glazingStyle'>,
): 'left' | 'right' {
  return config.glazingStyle === 'panorama' ? 'left' : 'right';
}

/**
 * The open panorama — one room, a glass wall, the entry in the long wall
 * opposite — is laid out to face the view, like the reference showpiece: an
 * L of lavar along the entry wall and round the far end, looking out through
 * the glass; the entry door in the entry wall's front corner; and the aggregat
 * on the front end wall, out by the glass, instead of beside the door with the
 * other half of the room left empty.
 */
export function isTwoRoomViewLayout(
  config: Pick<TwoRoomConfig, 'glazingStyle' | 'hasRelaxRoom' | 'entryWall'>,
): boolean {
  return (
    config.glazingStyle === 'panorama' &&
    !config.hasRelaxRoom &&
    config.entryWall === 'long'
  );
}

/**
 * View layout: the entry door's far jamb stands this far from the building's
 * outer front corner — clear of the corner posts and the end wall's lining.
 */
export const VIEW_LAYOUT_DOOR_CORNER_MM = 450;

/** View layout: the lavar stop this far short of the entry door's jamb. */
export const VIEW_LAYOUT_BENCH_DOOR_GAP_MM = 100;

/** View layout: the aggregat stands this far clear of the panorama glass. */
export const VIEW_LAYOUT_HEATER_GLASS_GAP_M = 0.45;

/**
 * Where the view layout stands the aggregat: backed onto the front end wall,
 * out toward the glass. Model metres; the unit's width runs along X there.
 */
export function getViewLayoutHeaterCenter(
  innerW: number,
  innerL: number,
  heaterW: number,
  heaterD: number,
  wallGapM: number,
): { x: number; z: number } {
  return {
    x: innerW / 2 - heaterW / 2 - VIEW_LAYOUT_HEATER_GLASS_GAP_M,
    z: innerL / 2 - heaterD / 2 - wallGapM,
  };
}
